import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings, Clock,
  MapPin, Camera
} from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';
import '../css/Profile.css';
import { getCurrentUserData } from '../helpers/user';
import { getUserFavorites } from '../helpers/favorites';
import { API_BASE_URL } from '../helpers/api';
import { uploadProfilePicture, updateProfilePicture } from '../helpers/user';

const Profile = () => {
  usePageTitle('Mon Profil');
  const [user, setUser] = useState(null);
  const [favorites, setFavorites] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const userData = await getCurrentUserData();
      setUser(userData);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchUserFavoritesa = async () => {
      if (user?.id) {
        const userFavorites = await getUserFavorites(user.id);
        // console.log(userFavorites);
        setFavorites(userFavorites);
      }
    };
    fetchUserFavoritesa();
  }, [user?.id]);

  const handleProfilePictureChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
  
    const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setError('Veuillez sélectionner un fichier image valide (JPEG, PNG ou GIF)');
      return;
    }
  
    if (file.size > 5 * 1024 * 1024) {
      setError("La taille du fichier doit être inférieure à 5 Mo");
      return;
    }
  
    try {
      setIsUploading(true);
      setError(null);
      
      const uploadedFile = await uploadProfilePicture(file);
      await updateProfilePicture(user.id, uploadedFile.id);
      
      const userData = await getCurrentUserData();
      setUser(userData);
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      setError("Échec de la mise à jour de la photo de profil. Veuillez réessayer.");
      console.error('Error updating profile picture:', error);
    } finally {
      setIsUploading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="profile-page">
      <div className="profile-hero">
        <div className="container">
          <h1>Mon Profil</h1>
        </div>
      </div>

      <div className="container">
        <div className="profile-grid">
          {/* Profile Overview */}
          <div className="profile-overview">
            <div className="profile-card">
              <div className="profile-header">
                <div className="profile-image-container">
                  <img 
                    src={API_BASE_URL+user.profilePicture.url} 
                    alt={user.name} 
                    className="profile-image" 
                  />
                  <label className="edit-profile-image btn btn-dark btn-sm rounded-circle" htmlFor="profile-picture-input">
                    <Camera size={20} />
                    <input
                      type="file"
                      id="profile-picture-input"
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                      style={{ display: 'none' }}
                      disabled={isUploading}
                    />
                  </label>
                  {/* <button className="edit-profile-image">
                    <Camera size={20} />
                  </button> */}
                </div>
                <div className="profile-info">
                  <h2>@{user.username}</h2>
                  <p className="username">{user.email}</p>
                  <p className="join-date">
                    <Clock size={16} /> Membre depuis le {new Date(user.createdAt).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              </div>

              <div className="profile-actions">
                <Link to="/profile/settings" className="profile-action-btn">
                  <Settings size={20} />
                  Paramètres
                </Link>
              </div>
            </div>
            {error && (
              <div className="alert alert-danger mt-3" role="alert">
                {error}
              </div>
            )}
            {success && (
              <div className="alert alert-success mt-3" role="alert">
                Photo de profil mise à jour avec succès !
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="profile-activity">

            {/* Favorite Establishments */}
            <div className="section-card">
              <div className="section-header">
                <h3>Mes spots favoris</h3>
                <Link to="/profile/favorites" className="view-all">Voir tout</Link>
              </div>

              <div className="favorites-grid">
                {favorites && favorites.length > 0 ? (
                  favorites.slice(0, 4).map((item, index) => (
                    <Link to={`/spot/${item.establishment.documentId}`} className='no-deco' key={index}>
                      <div className="favorite-card">
                        <img src={API_BASE_URL+item.establishment.images[0].url} alt={item.establishment.name} />
                        <div className="favorite-info">
                          <h4>{item.establishment.name}</h4>
                          <span>
                            <MapPin size={16} /> {item.establishment.address}, {item.establishment.portalCode} {item.establishment.city}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p>Aucun favoris pour le moment</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;